import React, { useEffect, useState } from 'react';
import { useParams, useLocation, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { HaleWordmark } from '../../components/HaleBrand';
import { CheckCircle, ArrowRight, Sparkle } from '@phosphor-icons/react';
import { HALE_BG, HALE_TEXT_MUTED, HALE_TEXT_FAINT, STANDARDS_NARRATIVE } from './_design';
import TI from '../../components/brand/TI';

const API = process.env.REACT_APP_BACKEND_URL;

/* /hale/pathway/:id — post-submit founder-facing screen.
 *
 * Pulls the recommendation either from router state (instant, no flash) or
 * by fetching the application record (when the founder lands here via the
 * magic-link email after closing the tab).
 *
 * Visual tone: editorial, celebratory, elevated. NO regulatory chrome.
 * Reads like a luxury membership welcome, not a compliance result page.
 */

const TRACK_LABEL = {
  hale_reviewed: 'HALE Reviewed™',
  hale_verified: 'HALE Verified™',
  both: 'HALE Reviewed™ + Verified™',
};
const TIER_LABEL = {
  startup: 'Startup',
  emerging: 'Emerging',
  enterprise: 'Enterprise',
};

function fmt(usd) {
  if (typeof usd !== 'number') return null;
  return `$${usd.toLocaleString()}`;
}

function PriceRange({ p }) {
  if (!p) return null;
  const ob = p.onboarding ? fmt(p.onboarding)
    : (p.onboarding_min ? `${fmt(p.onboarding_min)} – ${fmt(p.onboarding_max)}` : null);
  const mo = p.monthly ? `${fmt(p.monthly)}/mo`
    : (p.monthly_min ? `${fmt(p.monthly_min)} – ${fmt(p.monthly_max)}/mo` : null);
  return (
    <div className="space-y-2">
      {ob && (
        <p className="text-[11px] uppercase tracking-[0.22em] text-[#0B1426]/60">
          Onboarding · <span className="text-[#0B1426] font-bold tabular-nums">{ob}</span>
        </p>
      )}
      {mo && (
        <p className="text-[11px] uppercase tracking-[0.22em] text-[#0B1426]/60">
          Membership · <span className="text-[#0B1426] font-bold tabular-nums">{mo}</span>
        </p>
      )}
      {p.per_sku && (
        <p className="text-[11px] uppercase tracking-[0.22em] text-[#0B1426]/60">
          Per additional SKU · <span className="text-[#0B1426] font-bold tabular-nums">{fmt(p.per_sku)}</span>
        </p>
      )}
    </div>
  );
}

export default function HalePathwayPage() {
  const { id } = useParams();
  const location = useLocation();
  const [rec, setRec] = useState(location.state?.recommendation || null);
  const [magicLink, setMagicLink] = useState(location.state?.magic_link || null);
  const [loading, setLoading] = useState(!rec);

  useEffect(() => {
    if (rec) return;
    // Founder landed here from the email — magic_token comes in the query
    // string in that flow. For now we fall back to just showing a redirect
    // to /hale/membership where the auth handshake happens.
    axios.get(`${API}/api/hale-marketplace/applications/me`, { withCredentials: true })
      .then(r => { setRec(r.data?.recommendation); setLoading(false); })
      .catch(() => setLoading(false));
  }, [id, rec]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-[#0B1426]/50 text-sm"
           style={{ backgroundColor: HALE_BG }}>
        Loading your pathway…
      </div>
    );
  }

  if (!rec) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center text-[#0B1426]"
           style={{ backgroundColor: HALE_BG }}>
        <p className="text-sm text-[#0B1426]/60 mb-4">We couldn&apos;t load your pathway.</p>
        <Link to="/hale/apply">
          <Button className="rounded-full bg-[#E63946] text-black">Start a new application →</Button>
        </Link>
      </div>
    );
  }

  const narrative = STANDARDS_NARRATIVE[rec.track] || {};

  return (
    <div className="min-h-screen text-[#0B1426]" style={{ backgroundColor: HALE_BG }}>
      <header className="border-b px-6 lg:px-12 py-5" style={{ borderColor: 'rgba(11,20,38,0.06)' }}>
        <div className="max-w-6xl mx-auto"><HaleWordmark size="sm" /></div>
      </header>

      <main className="max-w-3xl mx-auto px-6 lg:px-0 py-20 lg:py-28">
        <div className="flex items-center gap-2 mb-8" data-testid="pathway-badge">
          <Sparkle size={14} weight="fill" style={{ color: '#E63946' }} />
          <p className="text-[10px] font-bold uppercase tracking-[0.32em] text-[#E63946]">
            Governance pathway · provisionally routed
          </p>
        </div>

        <h1 className="text-4xl lg:text-6xl leading-[1.05] tracking-tight font-light"
            style={{ fontFamily: '"Cormorant Garamond", "Times New Roman", serif' }}>
          You&apos;re a fit for<br/>
          <em className="not-italic" style={{ color: '#E63946', fontStyle: 'italic' }}>{TRACK_LABEL[rec.track]}</em>,<br/>
          {TIER_LABEL[rec.tier]} tier.
        </h1>

        {/* AI rationale paragraph — the LLM voice of the HALE concierge */}
        {rec.rationale && (
          <div className="mt-12 pl-6 border-l-2" style={{ borderColor: '#E63946' }} data-testid="pathway-rationale">
            <p className="text-lg leading-[1.65]" style={{ color: 'rgba(11,20,38,0.85)' }}>
              {rec.rationale}
            </p>
          </div>
        )}

        {/* Pricing block */}
        <div className="mt-16">
          <p className="text-[10px] font-bold uppercase tracking-[0.32em] text-[#0B1426]/40 mb-6">
            Membership · indicative pricing
          </p>
          {rec.track === 'both' ? (
            <div className="grid sm:grid-cols-2 gap-6">
              <div className="rounded-lg border p-6" style={{ borderColor: 'rgba(11,20,38,0.1)' }}>
                <p className="text-[10px] uppercase tracking-[0.22em] text-[#E63946] mb-2">Reviewed™</p>
                <PriceRange p={rec.pricing?.reviewed} />
              </div>
              <div className="rounded-lg border p-6" style={{ borderColor: 'rgba(11,20,38,0.1)' }}>
                <p className="text-[10px] uppercase tracking-[0.22em] text-[#E63946] mb-2">Verified™</p>
                <PriceRange p={rec.pricing?.verified} />
              </div>
            </div>
          ) : (
            <div className="rounded-lg border p-6 max-w-md" style={{ borderColor: 'rgba(11,20,38,0.1)' }}>
              <PriceRange p={rec.pricing} />
            </div>
          )}
          <p className="text-[11px] mt-4 max-w-lg" style={{ color: HALE_TEXT_FAINT }}>
            Final terms are scoped against SKU portfolio, retailer targets, and integration depth.
            Quote is confirmed during the reviewer onboarding call.
          </p>
        </div>

        {/* Drivers */}
        {rec.primary_drivers && rec.primary_drivers.length > 0 && (
          <div className="mt-16">
            <p className="text-[10px] font-bold uppercase tracking-[0.32em] text-[#0B1426]/40 mb-5">
              What routed you here
            </p>
            <ul className="space-y-3">
              {rec.primary_drivers.map((d, i) => (
                <li key={i} className="flex items-start gap-3" data-testid={`pathway-driver-${i}`}>
                  <CheckCircle size={16} weight="fill" className="mt-1 flex-shrink-0" style={{ color: '#E63946' }} />
                  <span className="text-[15px] text-[#0B1426]/80">{d}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* What happens next */}
        <div className="mt-16 rounded-lg p-7"
             style={{ backgroundColor: 'rgba(11,20,38,0.03)', border: '1px solid rgba(11,20,38,0.06)' }}>
          <p className="text-[10px] font-bold uppercase tracking-[0.32em] text-[#E63946] mb-3">What happens next</p>
          <ol className="space-y-3 text-[15px] text-[#0B1426]/85">
            <li><span className="font-mono text-[#0B1426]/40 mr-2">01</span>A ToxIntel reviewer is assigned within two business days.</li>
            <li><span className="font-mono text-[#0B1426]/40 mr-2">02</span>You&apos;ll receive a private link to your HALE governance workspace.</li>
            <li><span className="font-mono text-[#0B1426]/40 mr-2">03</span>Together we finalise scope, retailer mapping, and documentation overlay.</li>
            <li><span className="font-mono text-[#0B1426]/40 mr-2">04</span>Your governance infrastructure goes live in the marketplace.</li>
          </ol>
        </div>

        {/* CTA */}
        <div className="mt-12 flex gap-3 flex-wrap">
          {magicLink && (
            <Link to={magicLink} data-testid="pathway-resume">
              <Button className="h-11 px-7 text-[11px] font-bold uppercase tracking-[0.22em] rounded-full bg-[#E63946] hover:bg-[#C9192C] text-black">
                Open my governance workspace <ArrowRight size={14} className="ml-2" weight="bold" />
              </Button>
            </Link>
          )}
          <Link to="/hale/marketplace" data-testid="pathway-back">
            <Button variant="outline" className="h-11 px-7 text-[11px] font-bold uppercase tracking-[0.22em] rounded-full bg-transparent border-[#0B1426]/20 text-[#0B1426]/80 hover:bg-[#0B1426]/5">
              Back to marketplace
            </Button>
          </Link>
        </div>
      </main>

      <footer className="border-t px-6 py-8 mt-16" style={{ borderColor: 'rgba(11,20,38,0.06)' }}>
        <p className="text-center text-[10px] uppercase tracking-[0.22em]" style={{ color: HALE_TEXT_FAINT }}>
          HALE × <TI color="#E63946" /> · Governance activation · {narrative.short}
        </p>
        <p
          className="text-center text-[10px] mt-3 max-w-3xl mx-auto leading-relaxed"
          style={{ color: HALE_TEXT_FAINT }}
          data-testid="hale-platform-footnote"
        >
          HALE onboarding is powered by <TI color="#E63946" />. Scientific audit outputs are
          routed through the <TI color="#E63946" /> review infrastructure.
        </p>
      </footer>
    </div>
  );
}
